import { Fragment, useState } from "react";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./ListWithUsFaq.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * FAQ accordion — Figma node 2456:6682. The first item renders open, as in the design; one item
 * open at a time.
 *
 * ⚠️  SEVENTEEN QUESTIONS IN FOUR GROUPS, replacing a flat list of nineteen. The copy is supplied
 * rather than drafted this time — the previous set was written here against a brief, and none of it
 * survives verbatim. Notably the answers that used to be deliberately vague are now specific, and
 * that specificity comes from the brief rather than from this page:
 *   · go-live is "within a day of commercials being agreed", where the old answer said "a few
 *     working days" and carried a warning that it had never been verified;
 *   · PMS integration is now a plain yes, where the old answer named nothing on purpose;
 *   · offers are "partner funded", and pricing changes are made BY the account manager rather than
 *     by the partner in a dashboard — which contradicts what the Tools section's mock-ups imply.
 *     Worth checking those two against each other before this ships.
 *
 * Commission still states no rate, which remains correct: it is a per-partner commercial term.
 */

/**
 * ⚠️  THE GROUP LABEL IS A HEADING RENDERED BEFORE THE ROW, not a property of the question. Keeping
 * the items in one flat array is what lets the accordion stay index-based — `open` is an index, and
 * nesting the groups would mean a composite key and a rewrite of the open/close logic for no gain.
 *
 * ⚠️  "Getting started" IS INFERRED. The brief labels the last three groups and leaves the first five
 * questions unlabelled; with three headings below it, an unlabelled opening block reads as one that
 * lost its heading rather than one that never had one. Rename or drop it if that was deliberate.
 */
interface Item {
  /** Translation key for a group heading rendered above this row. */
  group?: string;
  /** Translation key for the question. */
  question: string;
  /** Translation key for the answer. */
  answer: string;
}

const ITEMS: Item[] = [
  {
    group: "list.v2.faq.group.gettingstarted",
    question: "list.v2.faq.q1.question",
    answer: "list.v2.faq.q1.answer",
  },
  {
    question: "list.v2.faq.q2.question",
    answer: "list.v2.faq.q2.answer",
  },
  {
    question: "list.v2.faq.q3.question",
    answer: "list.v2.faq.q3.answer",
  },
  {
    question: "list.v2.faq.q4.question",
    answer: "list.v2.faq.q4.answer",
  },
  {
    question: "list.v2.faq.q5.question",
    answer: "list.v2.faq.q5.answer",
  },

  {
    group: "list.v2.faq.group.costs",
    question: "list.v2.faq.q6.question",
    answer: "list.v2.faq.q6.answer",
  },
  {
    question: "list.v2.faq.q7.question",
    // ⚠️  STILL NO RATE, and that stays correct: commission is a per-partner commercial term and
    // not this page's to publish.
    answer: "list.v2.faq.q7.answer",
  },

  {
    group: "list.v2.faq.group.managing",
    question: "list.v2.faq.q8.question",
    answer: "list.v2.faq.q8.answer",
  },
  {
    question: "list.v2.faq.q9.question",
    answer: "list.v2.faq.q9.answer",
  },
  {
    question: "list.v2.faq.q10.question",
    answer: "list.v2.faq.q10.answer",
  },
  {
    question: "list.v2.faq.q11.question",
    answer: "list.v2.faq.q11.answer",
  },
  {
    question: "list.v2.faq.q12.question",
    answer: "list.v2.faq.q12.answer",
  },

  {
    group: "list.v2.faq.group.growth",
    question: "list.v2.faq.q13.question",
    answer: "list.v2.faq.q13.answer",
  },
  {
    question: "list.v2.faq.q14.question",
    answer: "list.v2.faq.q14.answer",
  },
  {
    question: "list.v2.faq.q15.question",
    answer: "list.v2.faq.q15.answer",
  },
  {
    question: "list.v2.faq.q16.question",
    answer: "list.v2.faq.q16.answer",
  },
  {
    question: "list.v2.faq.q17.question",
    answer: "list.v2.faq.q17.answer",
  },
];

const Faq = () => {
  const { t } = useTranslationHook();
  const [open, setOpen] = useState(0);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* The h2 alone. It carried a line under it — "Everything partners ask before they
            list. If it is not here, we will answer it on the call." — which `.headingBlock`
            existed to pair it with; both are gone. "Frequently asked questions" needs no
            introduction, and the promise about the call is the CTA's job two sections down. */}
        <Reveal as="h2" className={styles.heading}>
          {t("list.v2.faq.heading")}
        </Reveal>

        <Reveal className={styles.list} delay={100}>
          {ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              // The group heading sits OUTSIDE the row on purpose — inside it, it would be part of
              // the button and clicking the label would toggle the first question of its group.
              <Fragment key={item.question}>
                {item.group ? <h3 className={styles.group}>{t(item.group)}</h3> : null}

                <div className={`${styles.item} ${isOpen ? styles.itemOpen : ""}`}>
                  <button
                    type="button"
                    className={styles.trigger}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <span className={styles.question}>{t(item.question)}</span>
                    <span
                      className={`${styles.toggle} ${isOpen ? styles.toggleOpen : ""}`}
                      aria-hidden="true"
                    >
                      {isOpen ? "–" : "+"}
                    </span>
                  </button>

                  {isOpen ? <p className={styles.answer}>{t(item.answer)}</p> : null}
                </div>
              </Fragment>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
};

export default wrapperHOC(Faq, {
  componentName: "Faq-ListWithUs",
  showForChina: true,
});
